import { useQuery } from '@tanstack/react-query';
import { animate, motion, useMotionValue, useScroll, useTransform } from 'framer-motion';
import {
  ArrowRight,
  Banknote,
  CarFront,
  Fuel,
  Gift,
  MapPin,
  Receipt,
  ScanLine,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';
import { formatarData, formatBrl } from '@/lib/format';
import { supabase } from '@/lib/supabase';
import { useEnsureOnboarding } from '@/lib/use-ensure-onboarding';
import { useFeatureFlag } from '@/lib/use-feature-flag';

/**
 * HomePageV2 — TESTE Impeccable + BRANDING OCTA OFICIAL.
 *
 * Paleta oficial Octa Sistemas (~/.claude/topics/branding-octa.md):
 *   Laranja Ponto  #FF6A00 — ÚNICO accent, raro (10%)
 *   Azul Marinho   #111827 — texto principal, surfaces escuras
 *   Fundo Escuro   #080F14 — hero premium, dark mode
 *   Branco         #FFFFFF — fundo padrão
 *   Cinza Claro    #F2F4F7 — fundo secundário, cards alternados
 *   Cinza Médio    #9AA1A9 — texto secundário, divisores
 *
 * Regra Octa: laranja é a "assinatura" da marca (ponto do logo octa.).
 * Não usar em bg amplo. Apenas em ponto, accent, CTA, ícone protagonista.
 *
 * Motion: tween + ease-out-expo (sem spring/bounce).
 */

const EASE_OUT_EXPO: [number, number, number, number] = [0.16, 1, 0.3, 1];
const EASE_OUT_QUART: [number, number, number, number] = [0.25, 1, 0.5, 1];

// Tokens de cor Octa (centralizados pra facilitar manutenção)
const OCTA = {
  orange: '#FF6A00',
  navy: '#111827',
  bgDark: '#080F14',
  white: '#FFFFFF',
  grayLight: '#F2F4F7',
  grayMedium: '#9AA1A9',
} as const;

function CountUp({ value }: { value: number }) {
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => Math.round(v));
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const controls = animate(mv, value, {
      duration: 0.7,
      ease: EASE_OUT_EXPO,
    });
    const unsub = rounded.on('change', (v) => setDisplay(v));
    return () => {
      controls.stop();
      unsub();
    };
  }, [value, mv, rounded]);

  return <>{display}</>;
}

interface DashCliente {
  nom_cliente: string;
  qtd_veiculos: number;
  proximo_vencimento: string | null;
  val_proxima_cobranca: number | null;
  qtd_lavagens_mes: number;
  nom_plano: string | null;
}

export function HomePageV2() {
  const { user } = useAuth();

  useEnsureOnboarding();

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 300], [0, -40]);
  const heroOpacity = useTransform(scrollY, [0, 200], [1, 0.55]);

  const dash = useQuery({
    queryKey: ['cliente-home', user?.id],
    queryFn: async (): Promise<DashCliente> => {
      const { data: cli } = await supabase
        .from('tab_clientes')
        .select('seq_cliente, nom_cliente')
        .eq('cod_auth_user', user!.id)
        .single();
      const [veic, ass, lav] = await Promise.all([
        supabase
          .from('tab_veiculos')
          .select('seq_veiculo', { count: 'exact', head: true })
          .eq('seq_cliente', cli?.seq_cliente ?? 0)
          .eq('ind_ativo', true),
        supabase
          .from('tab_assinaturas')
          .select('dta_proximo_vencimento, val_mensalidade_atual, tab_planos(nom_plano)')
          .eq('seq_cliente', cli?.seq_cliente ?? 0)
          .eq('cod_status', 'ATIVA')
          .order('dta_inicio', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('tab_lavagens')
          .select('seq_lavagem', { count: 'exact', head: true })
          .gte(
            'dta_inicio',
            new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
          ),
      ]);
      const planoEmbutido = ass.data as { tab_planos?: { nom_plano?: string } } | null;
      return {
        nom_cliente: cli?.nom_cliente ?? '',
        qtd_veiculos: veic.count ?? 0,
        proximo_vencimento: ass.data?.dta_proximo_vencimento ?? null,
        val_proxima_cobranca: ass.data?.val_mensalidade_atual ?? null,
        qtd_lavagens_mes: lav.count ?? 0,
        nom_plano: planoEmbutido?.tab_planos?.nom_plano ?? null,
      };
    },
    enabled: !!user,
  });

  const combustivelAtivo = useFeatureFlag('MODULO_COMBUSTIVEL');

  // Atalhos: Veículos protagonista em LARANJA (único brand). Resto NEUTRO.
  // Cinza médio #9AA1A9 não é cor — é separador. Uso slate-800/700/600 nos demais.
  const acoes = [
    { label: 'Avulsa', href: '/avulso/comprar', icon: Banknote, cor: 'bg-slate-800' },
    ...(combustivelAtivo
      ? [{ label: 'Abastecer', href: '/abastecer', icon: Fuel, cor: 'bg-slate-700' }]
      : []),
    {
      label: 'Veículos',
      href: '/veiculos',
      icon: CarFront,
      cor: 'bg-[#FF6A00]', // ÚNICO LARANJA — protagonista Octa
    },
    { label: 'Mapa', href: '/mapa', icon: MapPin, cor: 'bg-slate-800' },
    { label: 'Histórico', href: '/lavagens', icon: Receipt, cor: 'bg-slate-700' },
    { label: 'Cupons', href: '/fidelidade', icon: Gift, cor: 'bg-slate-800' },
    { label: 'Privacidade', href: '/lgpd', icon: ShieldCheck, cor: 'bg-slate-700' },
  ];

  const primeiroNome = dash.data?.nom_cliente?.split(' ')[0];
  const horaAtual = new Date().getHours();
  const periodoSaudacao = horaAtual < 12 ? 'Bom dia' : horaAtual < 18 ? 'Boa tarde' : 'Boa noite';

  return (
    <div className="relative min-h-screen pb-24" style={{ backgroundColor: OCTA.grayLight }}>
      {/* Badge sticky com tom laranja claro (alusão sutil ao brand) */}
      <div
        className="sticky top-0 z-50 mx-4 mt-3 flex items-center justify-between rounded-full px-3 py-1.5 ring-1 backdrop-blur-md"
        style={{
          backgroundColor: 'rgba(255, 241, 229, 0.92)',
          borderColor: '#FFD4B0',
        }}
      >
        <span
          className="text-[10px] font-black uppercase tracking-[0.18em]"
          style={{ color: OCTA.orange }}
        >
          ⚡ Impeccable V2 — branding Octa
        </span>
        <Link
          to="/"
          className="text-[10px] font-bold underline-offset-2 transition-opacity duration-150 hover:opacity-70"
          style={{ color: OCTA.orange }}
        >
          ← Original
        </Link>
      </div>

      <div className="space-y-6 p-4 pt-6">
        {/* HERO PREMIUM — Fundo Escuro #080F14, nome em LARANJA #FF6A00 (assinatura brand) */}
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE_OUT_EXPO }}
          style={{
            y: heroY,
            opacity: heroOpacity,
            backgroundColor: OCTA.bgDark,
          }}
          className="relative overflow-hidden rounded-[2rem] p-8 text-white"
        >
          <p
            className="mb-4 text-[10px] font-black uppercase tracking-[0.32em]"
            style={{ color: OCTA.grayMedium }}
          >
            {periodoSaudacao}
          </p>
          <h1 className="text-[clamp(2.5rem,9vw,4rem)] font-black leading-[0.92] tracking-[-0.04em] text-white">
            olá,
            <br />
            <span style={{ color: OCTA.orange }}>{primeiroNome ?? 'cliente'}</span>
            <span style={{ color: OCTA.orange }}>.</span>
          </h1>
          <p className="mt-5 max-w-[28ch] text-base font-medium" style={{ color: OCTA.grayMedium }}>
            {dash.data?.qtd_lavagens_mes
              ? `${dash.data.qtd_lavagens_mes} lavagem(ns) este mês. Bora pra mais?`
              : 'Pronto pra deixar o carro brilhando?'}
          </p>
        </motion.section>

        {/* CTA — Azul Marinho #111827 + ScanLine em LARANJA #FF6A00 (accent) */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: 0.1 }}
        >
          <Link
            to="/gerar-codigo"
            className="group relative block overflow-hidden rounded-[2rem] p-7 text-white outline-none transition-all duration-300 [transition-timing-function:cubic-bezier(0.25,1,0.5,1)] hover:-translate-y-0.5 hover:shadow-2xl focus-visible:ring-4 active:scale-[0.98] active:duration-100"
            style={{
              backgroundColor: OCTA.navy,
              boxShadow: '0 10px 30px -10px rgba(17,24,39,0.5)',
            }}
            aria-label="Iniciar lavagem agora"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p
                  className="text-[10px] font-black uppercase tracking-[0.32em]"
                  style={{ color: OCTA.orange }}
                >
                  Comece agora
                </p>
                <p className="mt-2 text-[2.5rem] font-black leading-none tracking-[-0.03em] text-white">
                  Lavar agora
                </p>
                <p className="mt-2 text-sm font-medium" style={{ color: OCTA.grayMedium }}>
                  Gere o código e digite no totem.
                </p>
              </div>
              <div
                className="flex h-20 w-20 shrink-0 items-center justify-center rounded-3xl shadow-xl ring-4 transition-transform duration-300 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] group-hover:rotate-6 group-hover:scale-110"
                style={{
                  backgroundColor: OCTA.orange,
                  boxShadow: '0 8px 24px -4px rgba(255,106,0,0.45)',
                }}
              >
                <ScanLine className="h-10 w-10 text-white" />
              </div>
            </div>
          </Link>
        </motion.div>

        {/* KPIs — bg branco, número em azul marinho, ícone bg laranja (1 dos 2) */}
        <div className="grid grid-cols-2 gap-3">
          {dash.isLoading ? (
            <>
              <Skeleton className="h-32 rounded-[1.5rem]" />
              <Skeleton className="h-32 rounded-[1.5rem]" />
            </>
          ) : (
            <>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: 0.18 }}
              >
                <Card
                  variant="elevated"
                  className="rounded-[1.5rem] border-0 shadow-md transition-all duration-300 [transition-timing-function:cubic-bezier(0.25,1,0.5,1)] hover:-translate-y-1 hover:shadow-xl"
                  style={{ backgroundColor: OCTA.white }}
                >
                  <CardContent className="p-5">
                    <div
                      className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl"
                      style={{ backgroundColor: OCTA.orange }}
                    >
                      <CarFront className="h-5 w-5 text-white" />
                    </div>
                    <p
                      className="text-[5rem] font-black tabular-nums leading-[0.85] tracking-[-0.05em]"
                      style={{ color: OCTA.navy }}
                    >
                      <CountUp value={dash.data?.qtd_veiculos ?? 0} />
                    </p>
                    <p
                      className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em]"
                      style={{ color: OCTA.grayMedium }}
                    >
                      veículos
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: 0.26 }}
              >
                <Card
                  variant="elevated"
                  className="rounded-[1.5rem] border-0 shadow-md transition-all duration-300 [transition-timing-function:cubic-bezier(0.25,1,0.5,1)] hover:-translate-y-1 hover:shadow-xl"
                  style={{ backgroundColor: OCTA.white }}
                >
                  <CardContent className="p-5">
                    <div
                      className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl"
                      style={{ backgroundColor: OCTA.navy }}
                    >
                      <Sparkles className="h-5 w-5 text-white" />
                    </div>
                    <p
                      className="text-[5rem] font-black tabular-nums leading-[0.85] tracking-[-0.05em]"
                      style={{ color: OCTA.navy }}
                    >
                      <CountUp value={dash.data?.qtd_lavagens_mes ?? 0} />
                    </p>
                    <p
                      className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em]"
                      style={{ color: OCTA.grayMedium }}
                    >
                      este mês
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </>
          )}
        </div>

        {/* Cartão Assinatura — Fundo Escuro #080F14 + dot pulse LARANJA */}
        {dash.data?.proximo_vencimento && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: 0.34 }}
          >
            <Link
              to="/assinatura"
              className="group block outline-none focus-visible:ring-4"
              aria-label="Ver detalhes da assinatura"
            >
              <div
                className="relative overflow-hidden rounded-[2rem] p-6 text-white shadow-xl transition-all duration-300 [transition-timing-function:cubic-bezier(0.25,1,0.5,1)] hover:-translate-y-0.5 hover:shadow-2xl active:scale-[0.99] active:duration-100"
                style={{ backgroundColor: OCTA.bgDark }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <motion.span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: OCTA.orange }}
                      animate={{ opacity: [1, 0.4, 1] }}
                      transition={{ duration: 2, repeat: Infinity, ease: EASE_OUT_QUART }}
                    />
                    <p
                      className="text-[10px] font-black uppercase tracking-[0.32em]"
                      style={{ color: OCTA.orange }}
                    >
                      Assinatura ativa
                    </p>
                  </div>
                  <TrendingUp className="h-5 w-5" style={{ color: OCTA.grayMedium }} />
                </div>
                <p className="mt-3 text-2xl font-bold tracking-tight text-white">
                  {dash.data.nom_plano ?? 'Plano atual'}
                </p>
                <div className="mt-6 flex items-end justify-between gap-4">
                  <div>
                    <p
                      className="text-[9px] font-bold uppercase tracking-[0.28em]"
                      style={{ color: OCTA.grayMedium }}
                    >
                      Próximo
                    </p>
                    <p
                      className="mt-1 text-sm font-bold tabular-nums"
                      style={{ color: OCTA.grayLight }}
                    >
                      {formatarData(dash.data.proximo_vencimento)}
                    </p>
                  </div>
                  <p className="text-[3.5rem] font-black tabular-nums leading-none tracking-[-0.04em] text-white">
                    {formatBrl(dash.data.val_proxima_cobranca ?? 0)}
                  </p>
                </div>
                <div
                  className="mt-5 flex items-center gap-1 text-xs font-semibold"
                  style={{ color: OCTA.grayMedium }}
                >
                  Ver detalhes
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          </motion.div>
        )}

        {/* Atalhos — neutro slate. Apenas Veículos = laranja (único protagonista brand) */}
        <div>
          <h2
            className="mb-4 text-[10px] font-black uppercase tracking-[0.32em]"
            style={{ color: OCTA.grayMedium }}
          >
            Atalhos rápidos
          </h2>
          <motion.div
            className="grid grid-cols-3 gap-3"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.05, delayChildren: 0.4 } },
            }}
          >
            {acoes.map((a) => (
              <motion.div
                key={a.href}
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.4, ease: EASE_OUT_EXPO },
                  },
                }}
              >
                <Link
                  to={a.href}
                  className="group flex flex-col items-center gap-2 rounded-2xl p-3 shadow-sm transition-all duration-300 [transition-timing-function:cubic-bezier(0.25,1,0.5,1)] hover:-translate-y-1 hover:shadow-md active:scale-[0.96] active:duration-100"
                  style={{ backgroundColor: OCTA.white }}
                >
                  <div
                    className={`flex h-14 w-14 items-center justify-center rounded-full text-white shadow-md transition-transform duration-300 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110 ${a.cor}`}
                  >
                    <a.icon className="h-6 w-6" />
                  </div>
                  <p
                    className="text-center text-[11px] font-bold tracking-tight"
                    style={{ color: OCTA.navy }}
                  >
                    {a.label}
                  </p>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Banner "Indique" — Azul Marinho #111827 + accent Gift LARANJA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: 0.6 }}
        >
          <Link
            to="/fidelidade"
            className="group relative block overflow-hidden rounded-[2rem] p-6 text-white outline-none transition-all duration-300 [transition-timing-function:cubic-bezier(0.25,1,0.5,1)] hover:-translate-y-1 hover:shadow-xl focus-visible:ring-4 active:scale-[0.98] active:duration-100"
            style={{
              backgroundColor: OCTA.navy,
              boxShadow: '0 10px 30px -10px rgba(17,24,39,0.45)',
            }}
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <p
                  className="text-[10px] font-black uppercase tracking-[0.28em]"
                  style={{ color: OCTA.orange }}
                >
                  Programa fidelidade
                </p>
                <p className="mt-2 text-2xl font-black leading-tight tracking-tight text-white">
                  Indique amigos
                  <br />
                  <span style={{ color: OCTA.orange }}>ganhe lavagens grátis</span>
                </p>
              </div>
              <div
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl ring-2 transition-transform duration-500 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] group-hover:rotate-12"
                style={{
                  backgroundColor: OCTA.orange,
                  borderColor: 'rgba(255,255,255,0.2)',
                  boxShadow: '0 8px 20px -4px rgba(255,106,0,0.45)',
                }}
              >
                <Gift className="h-8 w-8 text-white" />
              </div>
            </div>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
